import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Icon } from 'js/design_system/Icon';
import { Tooltip } from 'js/design_system/new_tooltip/Tooltip';
import CloseIcon from 'js/design_system/icons/close.svg';
import {
  DeprecatedGrid,
  GridColumn,
} from 'js/design_system/grid/DeprecatedGrid';
import getResponsivenessTraits from 'js/header/responsivenessTraits';
import SearchBar from 'js/header/SearchBar/SearchBar';
import $image_url from 'js/utils/image_url';
import { currentUser } from 'js/utils/user';
import { get } from 'js/utils/api_utils';
import useScreenSize from 'js/utils/hooks/useScreenSize';
import useScrollLock from 'js/utils/hooks/useScrollLock';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import trackClick from 'js/utils/trackClick';

import AcademiaIcon from './AcademiaIcon';
import AccountMenu from './AccountMenu';
import AccountMenuButton from './AccountMenuButton';
import { barHeightsFor, resolveNavImpactStats } from './impact_stats/arms';
import ImpactNavBar from './impact_stats/nav_bar/ImpactNavBar';
import SmallAccountMenuButton from './SmallAccountMenuButton';
import ToolsMenu from './ToolsMenu';
import ToolsSidebar from './ToolsSidebar';
import { useCitationsColdStart } from './useCitationsColdStart';
import { usePaperViewsColdStart } from './usePaperViewsColdStart';
import { grantsMarketingIconButton } from './GrantsMarketingButton';
import ImpactNavButton from './ImpactNavButton';
import PodcastLibraryButton from './PodcastLibraryButton';
import PremiumUpsellButton from './PremiumUpsellButton';
import UploadButton from './UploadButton';
import {
  GreenDot,
  IconAnchor,
  IconButton,
  IconContainer,
  IconLabel,
  NotificationDot,
  RelativeIconContainer,
} from './buttonStyles';
import { setDocumentTitleCount } from './documentTitleCount';
import { EXPERIMENTAL_NAV_ITEMS } from './experimentalNavItems';

type ReactNode = import('react').ReactNode;
type PageName = import('js/header/responsivenessTraits').PageName;
type CoreSlot = import('./experimentalNavItems').CoreSlot;
type ExperimentalNavItem = import('./experimentalNavItems').ExperimentalNavItem;
type FocusedNavDynamicProps = import('./types').FocusedNavDynamicProps;
type NavImpactLinks = import('./types').NavImpactLinks;
type ImpactNavBarUser =
  import('./impact_stats/nav_bar/ImpactNavBar').ImpactNavBarUser;

// The nav switches to the mobile style below this value (equal to $ds-tablet).
// If changing this value must also update '.focused-nav-container' styles.
export const HAMBURGER_MENU_SIZE = 768; // px
const NAV_HEIGHT_DESKTOP = 80;
export const NAV_HEIGHT_MOBILE = 56;

const NavAndTools = styled.div<{ isMobileNav: boolean }>`
  position: relative;
  z-index: 10;
  height: ${NAV_HEIGHT_DESKTOP}px;
  &.isMobileNav {
    height: ${NAV_HEIGHT_MOBILE}px;
  }
`;

const NavContainer = styled.div<{ inDevStyle?: boolean; useGrid?: boolean }>`
  max-height: ${NAV_HEIGHT_DESKTOP}px;
  max-width: 100vw;
  background-color: var(--background-white, #ffffff);

  &.inDevStyle {
    background-color: aqua;
  }

  padding: 16px;
  &.useGrid {
    padding: 16px 0;
  }
`;

const MobileNavContainer = styled(NavContainer)<{ useGrid?: boolean }>`
  padding: 7px 12px;
  &.useGrid {
    padding: 7px 0;
  }
`;

const MobileSearchContainer = styled.div<{ hasBadge?: boolean }>`
  flex-grow: 1;
  min-width: 0;
  margin-left: var(--spacing-sm-24, 24px);

  // The account button's notification bubble overflows ~10px to its right
  // (right: -10px), eating into the 24px gap (leaving only ~14px of visual
  // space). When the badge is showing, widen the margin to clear it.
  &.hasBadge {
    margin-left: 28px;
  }
`;

const MobileUpsellContainer = styled.div`
  flex-shrink: 1;
  min-width: 0;
  max-width: 40%;
  overflow: hidden;
`;

const WidthContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  > * {
    margin: 0;
    white-space: nowrap;
    > * {
      white-space: initial;
    }
  }
  > * ~ * {
    margin: 0 0 0 16px;
  }

  @media (max-width: ${HAMBURGER_MENU_SIZE}px) {
    > * ~ * {
      margin: 0 0 0 8px;
    }
  }
`;

const SearchBarContainer = styled.div`
  flex: 1 1 auto;
`;

// `IconAnchorsContainer`'s width cap, and what one additive experimental item adds to it.
//
// This cap is independent of the responsive trim below and is not covered by it: trimming
// only happens while `screenWidth < maxWidth`, so on a wide viewport nothing is trimmed and
// a row that outgrows the cap simply overflows its own box into the next nav element — which
// is what put the Tools label under the premium CTA in production.
//
// Measured in Storybook at 1440px, per-icon pitch (label box plus its 2 × 8px margins):
// Home 49.3, Grants 54.8, Upload 56.0, Tools 45.2, Mentions 68.6, Analytics 68.8,
// CiteRight 68.6. The widest core set control can render is six icons = 342.7px, so the
// 380px base cap already carries ~37px of slack, and an additive item may be up to
// 380 + ADDITIVE_ITEM_ROW_WIDTH - 342.7 px of pitch before the row overflows again.
//
// 80 is therefore an allowance with margin, not a measurement: it covers CiteRight's 68.6px
// with room for a longer label, and leaves the bound above at ~117px of pitch (~101px of
// label). Over-granting is free — `flex: 0 1 auto` with `flex-grow: 0` sizes the box to its
// content, so a cap above the content never binds and today's row still renders at 411.3px.
// Under-granting is not: it silently reinstates the overflow this constant exists to prevent.
// Note this is deliberately unrelated to ADDITIVE_ITEM_TRIM_STEP, which is a budget figure.
const ICON_ROW_MAX_WIDTH = 380;
const ADDITIVE_ITEM_ROW_WIDTH = 80;

// `NavContainer`'s horizontal padding, spent before the row sees the viewport.
const NAV_HORIZONTAL_PADDING = 16;

// The narrowest nav container that still has room for an additive item's icon. The non-search
// part of the seven-icon row measures 911px, so 1040px leaves `SearchBarContainer` ~129px —
// what control gets at the narrowest capped container (970px → 127px). Below this the item is
// dropped instead of being taken out of the search bar. See `additiveItems` below.
const ADDITIVE_ITEM_MIN_CONTAINER = 1040;

// Stable identity so the memos below do not see a new array on every render.
const NO_ADDITIVE_ITEMS: ExperimentalNavItem[] = [];

const IconAnchorsContainer = styled.div`
  flex: 0 1 auto;
  display: flex;
  justify-content: space-between;
  /* Always overridden: the render site passes an inline max-width unconditionally — this
     same 380px for the core icon set, wider once an additive experimental item renders.
     Kept as the fallback for any future render site that does not pass one. */
  max-width: ${ICON_ROW_MAX_WIDTH}px;

  > * {
    margin: 0 8px;
  }
`;

const FullViewMenu = styled.div`
  position: fixed;
  top: calc(var(--nav-mobile-height, ${NAV_HEIGHT_MOBILE}px) + 2px);
  left: 0;
  width: 100vw;
  height: calc(100svh - var(--nav-mobile-height, ${NAV_HEIGHT_MOBILE}px) - 2px);
  overflow-y: scroll;
  overflow-x: hidden;
  background: white;
  padding: 16px;
  padding-bottom: 64px;
`;

const CloseMenuButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  margin: 0;
  padding: 12px 16px;
  border: none;
  background: transparent;
`;

const BottomMenu = styled.div`
  box-shadow: 0px -2px 4px 0px #00000014;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  background: white;
  flex: 1 1 auto;
  display: flex;
  justify-content: space-evenly;
  height: 48px;

  @media (max-width: 300px) {
    > *:nth-child(3) {
      display: none;
    }
  }
`;

const ImpactNavAndTools = styled.div`
  position: relative;
  z-index: 10;
  height: 64px;
`;

const RestrictedUserBannerContainer = styled(Body)`
  width: 100%;
  padding: 12px;
  background-color: var(--brand-colors-peach);
  color: var(--text-primary);
  text-align: center;
`;

const RestrictedUserBannerLink = styled(BodyLink)`
  margin-left: 8px;
`;

type RestrictedUserBannerProps = {
  restrictedUser?: RestrictedUserType;
  confirmAccountEmail?: string;
};

const RestrictedUserBanner = ({
  restrictedUser,
  confirmAccountEmail,
}: RestrictedUserBannerProps) => {
  if (!restrictedUser) {
    return null;
  }

  switch (restrictedUser) {
    case 'unconfirmed':
      return (
        <RestrictedUserBannerContainer>
          Please confirm your account. We&apos;ve sent a confirmation link to{' '}
          {confirmAccountEmail}.
          <RestrictedUserBannerLink underline href="/confirm/send_new_email">
            Resend
          </RestrictedUserBannerLink>
          <RestrictedUserBannerLink underline href="/confirm/sent#email">
            Change Email
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    case 'flagged':
      return (
        <RestrictedUserBannerContainer>
          Your account was flagged as suspicious by an automated system. If this
          was an error, please accept our apologies.
          <RestrictedUserBannerLink underline href="/user_unflag_requests/new">
            Request Unflagging
          </RestrictedUserBannerLink>
          <RestrictedUserBannerLink underline href="/settings">
            Account Settings
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    case 'flagged_pending':
      return (
        <RestrictedUserBannerContainer>
          Your account was flagged as suspicious, and your reconsideration
          request is awaiting manual review. We&apos;re sorry for any
          inconvenience.
          <RestrictedUserBannerLink underline href="/settings">
            Account Settings
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    default: {
      const exhaustiveCheck: never = restrictedUser;
      throw new Error(`Unhandled restricted user type: ${exhaustiveCheck}`);
    }
  }
};

type ResponsiveIcon = {
  icon: ReactNode;
  // Lower priority wins on smaller screens.
  priority: number;
};

// One responsive-trim step, in pixels: the width the trim math assumes an icon occupies.
const ADDITIVE_ITEM_TRIM_STEP = 50;

type ContainerProps = {
  useGrid: boolean;
  maxWidth: number | undefined;
  children: ReactNode;
};

const Container = ({ useGrid, maxWidth, children }: ContainerProps) =>
  useGrid ? (
    <DeprecatedGrid>
      <GridColumn sm={2} md={8} lg={12} xl={12}>
        <WidthContainer>{children}</WidthContainer>
      </GridColumn>
    </DeprecatedGrid>
  ) : (
    <WidthContainer style={{ maxWidth }}>{children}</WidthContainer>
  );

type Menu = 'account' | 'tools';

export type RestrictedUserType = 'unconfirmed' | 'flagged' | 'flagged_pending';

/**
 * Public props for the logged-in nav. These are the same props the server
 * passes through `react_component('FocusedNav', ...)`; the `FocusedNav` wrapper
 * accepts exactly this shape and supplies `dynamicProps` to the view.
 */
// Per-device flag: set the first time the user clicks the Co-scientist nav item,
// so the green "new" dot only shows until they've noticed the entry point.
export const CO_SCIENTIST_NAV_SEEN_KEY = 'co_scientist_nav_seen';

export type FocusedNavProps = {
  inDevStyle?: boolean;
  pageName: PageName;
  useGrid?: boolean;
  initialQuery?: string;
  isPremium?: boolean;
  hidePremiumUpsellButton?: boolean;
  restrictedUser?: RestrictedUserType;
  confirmAccountEmail?: string;
  showCitationsDashboard?: boolean;
  /**
   * When true, the leftmost nav item is "Co-scientist" (linking to /co-scientist/)
   * instead of "Home". Set server-side for users who have entered Co-scientist and
   * finished onboarding, to help them get back in (retention). SSR so there's no
   * Home→Co-scientist flash.
   */
  showCoScientistNav?: boolean;
  navImpactArm?: string;
  navImpactTestRunning?: boolean;
  navImpactLinks?: NavImpactLinks | null;
};

/**
 * Presentational logged-in nav. Owns all of the nav's rendering and UI state
 * (open menu, responsive icon trimming, scroll lock) but takes the async
 * `dynamicProps` payload — notifications, premium upsells, feature flags — as a
 * single injected object rather than fetching it. The live `FocusedNav` wrapper
 * supplies it from `useFocusedNavDynamicProps`; Storybook supplies a stub. This
 * is the seam that lets stories drive every state without fetch mocking.
 */
export type FocusedNavViewProps = FocusedNavProps & {
  dynamicProps?: FocusedNavDynamicProps;
  /**
   * Test seam: the experimental nav registry to render. Defaults to the live
   * `EXPERIMENTAL_NAV_ITEMS`; Storybook/tests can inject a synthetic list (e.g. to
   * exercise the replace path) without shipping it.
   */
  experimentalNavItems?: ExperimentalNavItem[];
  /**
   * Test seam: seeds the initial Mentions unread count. In production the count is
   * fetched from a live endpoint; Storybook/tests use this to demonstrate the badge.
   */
  initialMentionsCount?: number;
};

const FocusedNavView = ({
  inDevStyle,
  pageName,
  useGrid = false,
  initialQuery,
  isPremium = false,
  hidePremiumUpsellButton,
  restrictedUser,
  confirmAccountEmail,
  showCitationsDashboard,
  showCoScientistNav = false,
  navImpactArm,
  navImpactTestRunning = false,
  navImpactLinks = null,
  dynamicProps,
  experimentalNavItems = EXPERIMENTAL_NAV_ITEMS,
  initialMentionsCount = 0,
}: FocusedNavViewProps) => {
  const iconSize = 24;

  // Allow conditional rendering of components that aren't SSR-safe
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Green "new" dot on the Co-scientist item, shown until first click (mirrors
  // the Impact nav dot). Starts true so SSR/first paint shows it for new users;
  // the effect hides it for anyone who's already clicked.
  const [coScientistNavSeen, setCoScientistNavSeen] = useState(false);
  useEffect(() => {
    if (localStorage.getItem(CO_SCIENTIST_NAV_SEEN_KEY)) {
      setCoScientistNavSeen(true);
    }
  }, []);

  const notifications = dynamicProps?.notifications;
  const premiumUpsells = dynamicProps?.premium_upsells;
  const featureFlags = dynamicProps?.feature_flags;
  const impactStats = usePaperViewsColdStart(
    useCitationsColdStart(dynamicProps?.impact_stats, currentUser?.id),
    currentUser?.id,
  );

  // Feature flags loaded asynchronously
  const showGrantsButtonOnNav = featureFlags?.showGrantsButtonOnNav ?? false;
  const showImpactHubNav = featureFlags?.showImpactHubNav ?? false;
  const showPodcastLibraryNav = featureFlags?.showPodcastLibraryNav ?? false;
  const showCitationsDashboardNav = showCitationsDashboard ?? false;

  const navImpactSurfaces = useMemo(
    () => resolveNavImpactStats(navImpactArm),
    [navImpactArm],
  );
  const suppressImpactNavButton = navImpactSurfaces.statStyle !== 'none';
  const impactBarHeights = barHeightsFor(navImpactSurfaces);
  const [impactMenuOpen, setImpactMenuOpen] = useState(false);

  const impactNavUser = useMemo<ImpactNavBarUser>(() => {
    if (!isClient || currentUser == null) {
      return { name: '', avatarUrl: '', profileUrl: '' };
    }
    const affiliation = [
      currentUser.department?.name,
      currentUser.department?.university?.name,
    ]
      .filter(Boolean)
      .join(', ');
    return {
      name:
        currentUser.display_name ??
        `${currentUser.first_name} ${currentUser.last_name}`,
      affiliation: affiliation === '' ? null : affiliation,
      avatarUrl: $image_url(currentUser.photo),
      profileUrl: currentUser.url,
    };
  }, [isClient]);

  // Experimental nav items (declared in ./experimentalNavItems) whose feature flag
  // is enabled for this user. Each may take over a core slot (`replaces`) or be
  // purely additive. See renderExperimentalItem + the desktop/mobile assembly below.
  const enabledExperimentalItems = useMemo(
    () =>
      experimentalNavItems.filter(
        (item) =>
          (featureFlags?.[item.flag] ?? false) &&
          (!item.premiumOnly || isPremium),
      ),
    [experimentalNavItems, featureFlags, isPremium],
  );

  // Additive items (no `replaces`) are the ones that add an icon to the row rather than
  // taking over a core slot, so they are what the row's width has to grow for. Hoisted out
  // of the desktop `icons` memo because the row's cap is computed outside it.
  const eligibleAdditiveItems = useMemo(
    () => enabledExperimentalItems.filter((item) => !item.replaces),
    [enabledExperimentalItems],
  );

  const [openMenu, setOpenMenu] = useState<null | Menu>(null);
  const { screenWidth } = useScreenSize();
  const { maxWidth } = getResponsivenessTraits(pageName, screenWidth);

  // The responsive trim below sizes the row from `screenWidth`, but the row renders inside the
  // nav container, which `getResponsivenessTraits` caps well below the viewport on some pages
  // (970px between 992px and 1199px for the `stepDown` family, `analytics_dashboard` and
  // `friend_finder`). Where that cap binds, the icons the trim kept do not have the room it
  // assumed, and since `IconAnchorsContainer` cannot shrink below its own content the deficit
  // is taken out of `SearchBarContainer` instead: at a 970px container the seven-icon row
  // leaves the search bar 58px against control's 127px — a difference between the arms with
  // nothing to do with the item under test, on a metric a nav experiment gets read on.
  //
  // So drop additive items where the cap binds and what it leaves is too narrow for them.
  // Deliberately scoped to additive items rather than made a general container-aware trim:
  // control renders none, so every control trim point, cap and budget stays untouched by
  // definition, which is what makes this safe to land mid-test.
  //
  // Both halves of the condition are load-bearing. Without `availableWidth`, a narrow viewport
  // on an *uncapped* page would drop the item at widths where the trim has already shrunk the
  // row enough to leave plenty of room — at a 1000px viewport only four icons survive and the
  // search bar has 217px. On the `useGrid` path `maxWidth` is never applied (see `Container`)
  // and the grid column governs instead, so there is no cap to bind and the guard stays off.
  const availableWidth =
    screenWidth - (useGrid ? 0 : NAV_HORIZONTAL_PADDING * 2);
  const containerWidth = useGrid
    ? availableWidth
    : Math.min(availableWidth, maxWidth ?? Infinity);
  const additiveItems =
    containerWidth < Math.min(ADDITIVE_ITEM_MIN_CONTAINER, availableWidth)
      ? NO_ADDITIVE_ITEMS
      : eligibleAdditiveItems;

  const iconRowMaxWidth =
    ICON_ROW_MAX_WIDTH + additiveItems.length * ADDITIVE_ITEM_ROW_WIDTH;
  const toolsButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [mentionsCount, setMentionsCount] = useState(initialMentionsCount);

  const isMobileNav = screenWidth < HAMBURGER_MENU_SIZE;

  // Which icon to show at top of mobile nav
  const mobileTopIcon = useMemo(() => {
    if (restrictedUser) {
      return null;
    }
    if (isPremium) {
      return 'tools_top';
    }
    if (showPodcastLibraryNav) {
      return 'podcast_library_top';
    }
    return 'upsell_top';
  }, [isPremium, restrictedUser, showPodcastLibraryNav]);

  // A button to show next to the menu on desktop
  const desktopHighlightedButton = useMemo(() => {
    if (restrictedUser) {
      return null;
    }
    if (showPodcastLibraryNav) {
      return 'podcast_library';
    }
    if (!isPremium && !hidePremiumUpsellButton) {
      return 'premium_upsell';
    }
    return null;
  }, [
    hidePremiumUpsellButton,
    isPremium,
    restrictedUser,
    showPodcastLibraryNav,
  ]);

  useEffect(() => {
    const url = routes.unread_count_api_v0_notification_messages_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setNotificationCount(count));
  }, []);

  useEffect(() => {
    const url = routes.unread_count_api_v0_mentions_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setMentionsCount(count));
  }, []);

  // Unread counts for experimental items that declare an `unreadCount` source,
  // keyed by item.key. Generalizes the old per-feature radar effect.
  const [experimentalUnreadCounts, setExperimentalUnreadCounts] = useState<
    Record<string, number>
  >({});
  useEffect(() => {
    enabledExperimentalItems.forEach((item) => {
      if (!item.unreadCount) return;
      const { route, countField } = item.unreadCount;
      const url = route({ subdomain_param: 'api' });
      get(url).then((res) =>
        setExperimentalUnreadCounts((prev) => ({
          ...prev,
          [item.key]: res[countField] ?? 0,
        })),
      );
    });
  }, [enabledExperimentalItems]);

  const experimentalUnreadTotal = Object.values(
    experimentalUnreadCounts,
  ).reduce((sum, count) => sum + count, 0);

  // Citations-dashboard users get a dot (not a number) for new mentions, so the
  // mentions count is excluded from the account badge total.
  const visibleMentionsCount = showCitationsDashboardNav ? 0 : mentionsCount;
  const totalCount =
    notificationCount +
    visibleMentionsCount +
    experimentalUnreadTotal +
    (notifications?.messagesCount ?? 0) +
    (notifications?.hasUnseenReaders ? 1 : 0);

  // SSR-safe: Update document title in useEffect to avoid hydration mismatch
  useEffect(() => {
    setDocumentTitleCount(totalCount);
  }, [totalCount]);

  const toggleMenu = useCallback(
    (menu: Menu): void => {
      if (openMenu === menu) {
        setOpenMenu(null);
      } else {
        setOpenMenu(menu);
      }
    },
    [openMenu],
  );

  const homeButton = useMemo(
    () => (
      <IconAnchor
        key="home"
        href={routes.home_url({ from_navbar: true, trigger: 'nav' })}
      >
        <IconContainer>
          <Icon name="home" size={iconSize} />
        </IconContainer>
        <IconLabel>Home</IconLabel>
      </IconAnchor>
    ),
    [iconSize],
  );

  // Co-scientist entry point that replaces Home for onboarded co-scientist users.
  // Green dot shows on first visit (until clicked) OR whenever there are unread
  // co-scientist notifications (same unread signal as the in-app badge count).
  const coScientistButton = useMemo(() => {
    // First visit: a green "New" tooltip announcing the entry point, shown until
    // the user visits Co-scientist (client-only; the seen flag is localStorage).
    // After that: a red notification dot when there are unread co-scientist
    // notifications — the same unread signal as the in-app badge count.
    //
    // Hidden while a full-screen menu is open: the tooltip is a body-level
    // Popover at the same z-index as the menus, so it would float over them
    // detached from its (now covered) anchor.
    const overlayOpen = openMenu !== null || impactMenuOpen;
    const showNewTooltip = isClient && !coScientistNavSeen && !overlayOpen;
    const showNotificationDot =
      coScientistNavSeen &&
      (notifications?.hasUnreadCoScientistNotifications ?? false);
    const item = (
      <IconAnchor
        href="/co-scientist/"
        onClick={() => {
          trackClick({ source: 'nav-bar--co-scientist' });
          if (!coScientistNavSeen) {
            localStorage.setItem(CO_SCIENTIST_NAV_SEEN_KEY, 'true');
            setCoScientistNavSeen(true);
          }
        }}
      >
        <IconContainer>
          <Icon name="home_repair_service" size={iconSize} />
        </IconContainer>
        <IconLabel>
          Co-scientist
          {showNotificationDot && <NotificationDot />}
        </IconLabel>
      </IconAnchor>
    );
    return (
      <ImpressionWatcher key="co-scientist" source="co_scientist.nav">
        {showNewTooltip ? (
          <Tooltip
            open
            placement="bottom"
            tooltipColor="var(--success-dark, #05b01c)"
            render={() => 'New'}
          >
            {item}
          </Tooltip>
        ) : (
          item
        )}
      </ImpressionWatcher>
    );
  }, [
    isClient,
    coScientistNavSeen,
    openMenu,
    impactMenuOpen,
    notifications?.hasUnreadCoScientistNotifications,
    iconSize,
  ]);

  const impactButton = useMemo(() => <ImpactNavButton />, []);

  const mentionsButton = useMemo(() => {
    if (showCitationsDashboardNav) {
      return (
        <ImpressionWatcher key="citations" source="citations.nav">
          <IconAnchor
            href={routes.citations_url({ from_navbar: true, trigger: 'nav' })}
          >
            <IconContainer>
              <Icon name="format_quote" size={iconSize} />
            </IconContainer>
            <IconLabel>
              Citations
              {mentionsCount > 0 && <NotificationDot />}
            </IconLabel>
          </IconAnchor>
        </ImpressionWatcher>
      );
    }

    return (
      <ImpressionWatcher key="mentions" source="mentions.nav">
        <IconAnchor
          href={routes.mentions_url({ from_navbar: true, trigger: 'nav' })}
        >
          <IconContainer>
            <Icon name="format_quote" size={iconSize} />
          </IconContainer>
          <IconLabel>
            Mentions
            {mentionsCount > 0 && <NotificationDot />}
          </IconLabel>
        </IconAnchor>
      </ImpressionWatcher>
    );
  }, [mentionsCount, showCitationsDashboardNav, iconSize]);

  const analyticsButton = useMemo(
    () =>
      isClient ? (
        <IconAnchor
          key="analytics"
          href={routes.analytics_dashboard_url({
            page_name: currentUser?.page_name,
            subdomain: currentUser?.domain_name,
            from_navbar: true,
            trigger: 'nav',
          })}
        >
          <IconContainer>
            <Icon name="bar_chart" size={iconSize} />
          </IconContainer>
          <IconLabel>Analytics</IconLabel>
        </IconAnchor>
      ) : null,
    [isClient, iconSize],
  );

  const uploadButton = useMemo(
    () => (
      <UploadButton
        key="upload"
        hasUnseenClaimablePapers={
          notifications?.hasUnseenClaimablePapers ?? false
        }
        hasUnseenUploadableStubs={
          notifications?.hasUnseenUploadableStubs ?? false
        }
      />
    ),
    [
      notifications?.hasUnseenClaimablePapers,
      notifications?.hasUnseenUploadableStubs,
    ],
  );

  const toolsButton = useMemo(
    () => (
      <IconButton
        ref={toolsButtonRef}
        key="tools"
        onClick={() => toggleMenu('tools')}
        aria-pressed={openMenu === 'tools'}
      >
        <IconContainer>
          <Icon name="apps" size={iconSize} />
        </IconContainer>
        <IconLabel>Tools</IconLabel>
      </IconButton>
    ),
    [openMenu, toggleMenu, iconSize],
  );

  const grantsMultiplierButton = useMemo(() => grantsMarketingIconButton, []);

  // Renders a registry-declared experimental item as a standard nav icon. `source`
  // distinguishes the desktop vs mobile instance (for analytics + React keys).
  const renderExperimentalItem = useCallback(
    (item: ExperimentalNavItem, source: 'desktop' | 'mobile'): ReactNode => {
      const unread = experimentalUnreadCounts[item.key] ?? 0;
      const showUnreadDot = Boolean(item.unreadCount) && unread > 0;
      return (
        <ImpressionWatcher
          key={`${item.key}-${source}`}
          source={item.impressionSource ?? `${item.key}.nav`}
        >
          <IconAnchor
            href={item.route({ from_navbar: true, trigger: 'nav' })}
            onClick={() => item.onNavClick?.(source)}
          >
            <RelativeIconContainer>
              <Icon name={item.iconName} size={24} />
              {(item.showNewDot ?? true) && <GreenDot />}
              {showUnreadDot &&
                (item.unreadCount?.dotColor === 'green' ? (
                  <GreenDot />
                ) : (
                  <NotificationDot />
                ))}
            </RelativeIconContainer>
            <IconLabel>{item.label}</IconLabel>
          </IconAnchor>
        </ImpressionWatcher>
      );
    },
    [experimentalUnreadCounts],
  );

  const icons = useMemo(() => {
    const showImpactHub =
      showImpactHubNav && !showGrantsButtonOnNav && !suppressImpactNavButton;
    // Grants icon for everyone; experimental items via the registry.
    const showGrantMultiplier = !isMobileNav && showGrantsButtonOnNav;

    // Core slots an experimental item may take over on desktop.
    const slotNodes: Record<CoreSlot, ReactNode> = {
      home: showCoScientistNav ? coScientistButton : homeButton,
      mentions: mentionsButton,
      analytics: analyticsButton,
      upload: uploadButton,
    };
    enabledExperimentalItems.forEach((item) => {
      if (item.replaces) {
        slotNodes[item.replaces] = renderExperimentalItem(item, 'desktop');
      }
    });

    const unfilteredIcons: (ResponsiveIcon | false)[] = [
      { icon: slotNodes.home, priority: 5 },
      showImpactHub && { icon: impactButton, priority: 2 },
      { icon: slotNodes.mentions, priority: 0 },
      showGrantMultiplier && { icon: grantsMultiplierButton, priority: 4 },
      { icon: slotNodes.analytics, priority: 1 },
      // Additive experimental items render after the core nav icons, before the
      // Upload/Tools action cluster.
      ...additiveItems.map((item) => ({
        icon: renderExperimentalItem(item, 'desktop'),
        priority: item.priority,
      })),
      { icon: slotNodes.upload, priority: 3 },
      { icon: toolsButton, priority: -Infinity },
    ];
    const icons: ResponsiveIcon[] = unfilteredIcons.filter(
      // Type guard is needed here to convince typescript that there are no false values
      (icon): icon is ResponsiveIcon => !!icon,
    );

    if (isPremium || hidePremiumUpsellButton) {
      return icons.map((icon) => icon.icon);
    }

    // Hide another icon every 50 pixels past the max width.
    const iconsWithPriority = icons.filter(
      (icon) => icon.priority !== undefined,
    ).length;
    const baseMaxWidth = 950; // Arbitrary pixels that respond best
    const maxWidth =
      baseMaxWidth +
      (premiumUpsells?.reactivatePremiumUpsellType ? 273 : 127) + // Premium button sizes
      // maxLevel counts icons but the budget above is a constant, so an additive
      // experimental item would otherwise raise the threshold without paying for the width
      // it takes: the arm would keep one more icon AND start trimming 50px later, showing
      // two more icons than control around 1000-1077px. Buying a trim step per additive
      // item makes the arm exactly control-plus-the-new-icon at every width.
      // Items with `replaces` take over an existing slot, so they cost nothing here.
      additiveItems.length * ADDITIVE_ITEM_TRIM_STEP;
    const maxLevel =
      iconsWithPriority -
      Math.floor((maxWidth - Math.min(screenWidth, maxWidth)) / 50);
    return icons
      .filter((icon) => icon.priority <= maxLevel)
      .map((icon) => icon.icon);
  }, [
    premiumUpsells?.reactivatePremiumUpsellType,
    screenWidth,
    analyticsButton,
    homeButton,
    coScientistButton,
    showCoScientistNav,
    impactButton,
    mentionsButton,
    uploadButton,
    toolsButton,
    hidePremiumUpsellButton,
    isMobileNav,
    grantsMultiplierButton,
    showGrantsButtonOnNav,
    showImpactHubNav,
    suppressImpactNavButton,
    isPremium,
    enabledExperimentalItems,
    additiveItems,
    renderExperimentalItem,
  ]);

  // Mobile bottom-bar experimental placement: replace a core item in the bar
  // (home/mentions/analytics) when targeted, otherwise append as a trailing tab.
  const mobileExperimental = useMemo(() => {
    const MOBILE_BAR_SLOTS: CoreSlot[] = ['home', 'mentions', 'analytics'];
    const slotOverrides: Partial<Record<CoreSlot, ReactNode>> = {};
    const appended: ReactNode[] = [];
    enabledExperimentalItems
      .filter((item) => item.showInMobile !== false)
      .forEach((item) => {
        if (item.replaces && MOBILE_BAR_SLOTS.includes(item.replaces)) {
          slotOverrides[item.replaces] = renderExperimentalItem(item, 'mobile');
        } else {
          appended.push(renderExperimentalItem(item, 'mobile'));
        }
      });
    return { slotOverrides, appended };
  }, [enabledExperimentalItems, renderExperimentalItem]);

  useScrollLock(isMobileNav && (openMenu !== null || impactMenuOpen));

  const closeMenuButton = (
    <CloseMenuButton onClick={() => setOpenMenu(null)}>
      <CloseIcon color="#222222" />
    </CloseMenuButton>
  );

  const renderImpactNavBar = (links: NavImpactLinks) => (
    <ImpactNavBar
      arm={navImpactArm}
      user={impactNavUser}
      impactStats={impactStats}
      badgeCount={totalCount}
      messagesCount={notifications?.messagesCount ?? 0}
      notificationCount={notificationCount}
      isPremium={isPremium}
      premiumUpsells={premiumUpsells}
      hidePremiumUpsellButton={hidePremiumUpsellButton}
      showPodcastLibraryNav={showPodcastLibraryNav}
      hasUnseenClaimablePapers={
        notifications?.hasUnseenClaimablePapers ?? false
      }
      hasUnseenUploadableStubs={
        notifications?.hasUnseenUploadableStubs ?? false
      }
      initialQuery={initialQuery}
      isMobileNav={isMobileNav}
      onMenuToggle={setImpactMenuOpen}
      testRunning={navImpactTestRunning}
      hasUnseenMentions={notifications?.hasUnseenMentions ?? false}
      hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
      links={links}
    />
  );

  if (isMobileNav) {
    const bottomMenu = !restrictedUser && (
      <BottomMenu>
        {mobileExperimental.slotOverrides.home ??
          (showCoScientistNav ? coScientistButton : homeButton)}
        {showImpactHubNav &&
          !showGrantsButtonOnNav &&
          !suppressImpactNavButton &&
          impactButton}
        {mobileExperimental.slotOverrides.mentions ?? mentionsButton}
        {mobileExperimental.slotOverrides.analytics ?? analyticsButton}
        {mobileTopIcon !== 'tools_top' ? toolsButton : uploadButton}
        {mobileExperimental.appended}
      </BottomMenu>
    );

    if (impactBarHeights != null && navImpactLinks != null) {
      return (
        <>
          <ImpactNavAndTools>
            {renderImpactNavBar(navImpactLinks)}
            {openMenu === 'tools' && (
              <FullViewMenu>
                {closeMenuButton}
                <ToolsMenu
                  hasUnseenClaimablePapers={
                    notifications?.hasUnseenClaimablePapers ?? false
                  }
                  hasUnseenUploadableStubs={
                    notifications?.hasUnseenUploadableStubs ?? false
                  }
                  isPremium={isPremium}
                />
              </FullViewMenu>
            )}
            {bottomMenu}
          </ImpactNavAndTools>
          <RestrictedUserBanner
            restrictedUser={restrictedUser}
            confirmAccountEmail={confirmAccountEmail}
          />
        </>
      );
    }

    return (
      <>
        <NavAndTools isMobileNav={isMobileNav}>
          <MobileNavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
            <Container maxWidth={maxWidth} useGrid={useGrid}>
              <SmallAccountMenuButton
                count={totalCount}
                menuOpen={openMenu === 'account'}
                setMenuOpen={(open) => setOpenMenu(open ? 'account' : null)}
              />
              <MobileSearchContainer hasBadge={totalCount > 0}>
                <ImpressionWatcher source="focused_nav_search_bar">
                  <SearchBar
                    initialQuery={initialQuery}
                    isPremium={isPremium}
                  />
                </ImpressionWatcher>
              </MobileSearchContainer>
              {mobileTopIcon === 'tools_top' && (
                <div onClick={() => toggleMenu('tools')}>{toolsButton}</div>
              )}
              {mobileTopIcon === 'upsell_top' && (
                <MobileUpsellContainer>
                  <PremiumUpsellButton
                    premiumUpsells={premiumUpsells}
                    isMobileNav={isMobileNav}
                  />
                </MobileUpsellContainer>
              )}
              {mobileTopIcon === 'podcast_library_top' && (
                <PodcastLibraryButton />
              )}
            </Container>
          </MobileNavContainer>
          {openMenu === 'tools' && (
            <FullViewMenu>
              {closeMenuButton}
              <ToolsMenu
                hasUnseenClaimablePapers={
                  notifications?.hasUnseenClaimablePapers ?? false
                }
                hasUnseenUploadableStubs={
                  notifications?.hasUnseenUploadableStubs ?? false
                }
                isPremium={isPremium}
              />
            </FullViewMenu>
          )}
          {openMenu === 'account' && (
            <FullViewMenu>
              {closeMenuButton}
              <AccountMenu
                restrictedUser={Boolean(restrictedUser)}
                halfOffCoupon={premiumUpsells?.halfOffCoupon}
                reactivate={premiumUpsells?.reactivatePremiumUpsellType}
                mentionsCount={mentionsCount}
                messagesCount={notifications?.messagesCount ?? 0}
                hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
                notificationCount={notificationCount}
                showCitationsDashboard={showCitationsDashboardNav}
                isPremium={isPremium}
              />
            </FullViewMenu>
          )}
          {bottomMenu}
        </NavAndTools>
        <RestrictedUserBanner
          restrictedUser={restrictedUser}
          confirmAccountEmail={confirmAccountEmail}
        />
      </>
    );
  }

  if (impactBarHeights != null && navImpactLinks != null) {
    return (
      <>
        <ImpactNavAndTools>
          {renderImpactNavBar(navImpactLinks)}
        </ImpactNavAndTools>
        <RestrictedUserBanner
          restrictedUser={restrictedUser}
          confirmAccountEmail={confirmAccountEmail}
        />
      </>
    );
  }

  return (
    <>
      <NavAndTools isMobileNav={isMobileNav}>
        <NavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
          <Container maxWidth={maxWidth} useGrid={useGrid}>
            <AcademiaIcon />
            <SearchBarContainer>
              <ImpressionWatcher source="focused_nav_search_bar">
                <SearchBar initialQuery={initialQuery} isPremium={isPremium} />
              </ImpressionWatcher>
            </SearchBarContainer>
            {!restrictedUser && (
              <IconAnchorsContainer
                data-testid="nav-icon-row"
                style={{ maxWidth: iconRowMaxWidth }}
              >
                {icons}
              </IconAnchorsContainer>
            )}
            {desktopHighlightedButton === 'premium_upsell' && (
              <PremiumUpsellButton
                premiumUpsells={premiumUpsells}
                isMobileNav={isMobileNav}
              />
            )}
            {desktopHighlightedButton === 'podcast_library' && (
              <PodcastLibraryButton />
            )}
            <AccountMenuButton
              restrictedUser={Boolean(restrictedUser)}
              halfOffCoupon={premiumUpsells?.halfOffCoupon}
              reactivate={premiumUpsells?.reactivatePremiumUpsellType}
              totalCount={totalCount}
              notificationCount={notificationCount}
              mentionsCount={mentionsCount}
              messagesCount={notifications?.messagesCount ?? 0}
              hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
              showCitationsDashboard={showCitationsDashboardNav}
              isPremium={isPremium}
              open={openMenu === 'account'}
              setOpen={(open) => setOpenMenu(open ? 'account' : null)}
            />
          </Container>
        </NavContainer>

        {openMenu === 'tools' && (
          <ToolsSidebar
            buttonRef={toolsButtonRef}
            close={() => setOpenMenu(null)}
            hasUnseenClaimablePapers={
              notifications?.hasUnseenClaimablePapers ?? false
            }
            hasUnseenUploadableStubs={
              notifications?.hasUnseenUploadableStubs ?? false
            }
            isPremium={isPremium}
          />
        )}
      </NavAndTools>
      <RestrictedUserBanner
        restrictedUser={restrictedUser}
        confirmAccountEmail={confirmAccountEmail}
      />
    </>
  );
};

export default FocusedNavView;
